/**
 * Who is in the watched areas — presence, not a heartbeat log.
 *
 * Enter ticks while someone stays put are one occupancy. An exit closes it.
 * Watchlisted occupants pin above the rest so a stay cannot drown them.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { geofenceService, Geofence } from '../../services/geofenceService';
import {
  Mission,
  MissionGeofence,
  MissionGeofenceEvent,
  getMissionGeofenceActivity,
  replaceMissionGeofences,
} from '../../services/missionApi';
import { formatSpan, formatWhen } from './format';
import { areaBadgeCount, buildAreaPresence, AreaPresence } from './areaPresence';
import { Action, MUTED, RED, TEAL } from './missionLiveParts';
import DockStatStrip from './DockStatStrip';
import { DockTabHelp } from './DockTabHelp';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PickerDialog from './PickerDialog';

export interface MissionAreasTabProps {
  mission: Mission;
  deviceLabel: (deviceId: string) => string;
  onFocus?: (longitude: number, latitude: number) => void;
  onCount?: (count: number) => void;
  /** Labels already known to be watchlisted, so they surface above ordinary stays. */
  watchlist?: string[];
  /** Start drawing a new fence on the mission map. */
  onDrawArea?: () => void;
  /** Re-read the mission after watch/unwatch. */
  onMissionChange?: () => void;
}

const EDGE = '#262b33';

function fenceCenter(fence: MissionGeofence): { longitude: number; latitude: number } | null {
  const ring = fence.geometry?.coordinates?.[0];
  if (!ring || ring.length < 1) return null;
  let lng = 0;
  let lat = 0;
  ring.forEach(([x, y]) => { lng += x; lat += y; });
  return { longitude: lng / ring.length, latitude: lat / ring.length };
}

const MissionAreasTab: React.FC<MissionAreasTabProps> = ({
  mission,
  deviceLabel,
  onFocus,
  onCount,
  watchlist = [],
  onDrawArea,
  onMissionChange,
}) => {
  const [activity, setActivity] = useState<MissionGeofenceEvent[]>([]);
  const [picker, setPicker] = useState(false);
  const [catalog, setCatalog] = useState<Geofence[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mission.geofences.length === 0 || mission.assignees.length === 0) {
      setActivity([]);
      onCount?.(0);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      getMissionGeofenceActivity(mission.id)
        .then((rows) => {
          if (cancelled) return;
          setActivity(rows);
        })
        .catch(() => {
          if (cancelled) return;
          setActivity([]);
        });
    };
    load();
    const timer = window.setInterval(load, 30000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [mission.id, mission.geofences.length, mission.assignees.length]);

  const feed = useMemo(
    () => buildAreaPresence(activity, deviceLabel, watchlist),
    [activity, deviceLabel, watchlist],
  );

  useEffect(() => {
    onCount?.(areaBadgeCount(feed));
  }, [feed, onCount]);

  const openPicker = () => {
    geofenceService.getGeofences(undefined, true)
      .then(setCatalog)
      .catch(() => setCatalog([]));
    setPicker(true);
  };

  const applyWatch = async (ids: number[]) => {
    setSaving(true);
    try {
      await replaceMissionGeofences(mission.id, ids);
      onMissionChange?.();
      setPicker(false);
    } finally {
      setSaving(false);
    }
  };

  const unwatch = (geofenceId: number) => {
    applyWatch(mission.geofences.map((g) => g.geofence_id).filter((id) => id !== geofenceId));
  };

  const tools = (
    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
      <Action onClick={openPicker}>Watch</Action>
      {onDrawArea && (
        <Action onClick={onDrawArea}>Draw</Action>
      )}
    </Box>
  );

  if (mission.geofences.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Geofences
          </Typography>
          <DockTabHelp tab="areas" />
          <Box sx={{ flex: 1 }} />
          {tools}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Nobody is being watched against a fence. Watch an existing geofence or draw one on the map.
        </Typography>
        <PickerDialog
          open={picker}
          title="Watch geofences"
          subtitle="Enter and exit are logged for assigned kit."
          options={catalog.map((f) => ({
            id: String(f.id),
            primary: f.name,
            secondary: f.description || undefined,
            note: f.active === false ? 'inactive' : undefined,
          }))}
          selected={[]}
          emptyText="No geofences defined yet."
          saving={saving}
          onClose={() => setPicker(false)}
          onSave={(ids) => { applyWatch(ids.map(Number)); }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {mission.geofences.length} fence{mission.geofences.length === 1 ? '' : 's'} watched
        </Typography>
        <DockTabHelp tab="areas" />
        <Box sx={{ flex: 1 }} />
        {tools}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {mission.geofences.map((g) => {
          const center = fenceCenter(g);
          return (
            <Box
              key={g.id}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.35,
                px: 0.75,
                py: 0.15,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontSize: '0.65rem',
              }}
            >
              <Typography
                variant="caption"
                role={center && onFocus ? 'button' : undefined}
                tabIndex={center && onFocus ? 0 : undefined}
                onClick={() => { if (center) onFocus?.(center.longitude, center.latitude); }}
                sx={{ fontSize: '0.65rem', cursor: center && onFocus ? 'pointer' : 'default' }}
              >
                {g.name}
              </Typography>
              <Box
                role="button"
                tabIndex={0}
                title={`Stop watching ${g.name}`}
                onClick={() => unwatch(g.geofence_id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); unwatch(g.geofence_id); }
                }}
                sx={{
                  display: 'flex',
                  color: MUTED,
                  cursor: 'pointer',
                  '&:hover': { color: RED },
                }}
              >
                <CloseIcon sx={{ fontSize: 12 }} />
              </Box>
            </Box>
          );
        })}
      </Box>
      <PickerDialog
        open={picker}
        title="Watch geofences"
        subtitle="Enter and exit are logged for assigned kit."
        options={catalog.map((f) => ({
          id: String(f.id),
          primary: f.name,
          secondary: f.description || undefined,
          note: f.active === false ? 'inactive' : undefined,
        }))}
        selected={mission.geofences.map((g) => String(g.geofence_id))}
        emptyText="No geofences defined yet."
        saving={saving}
        onClose={() => setPicker(false)}
        onSave={(ids) => { applyWatch(ids.map(Number)); }}
      />

      {mission.assignees.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Assign someone to see who is in these fences.
        </Typography>
      ) : (
        <>
          <DockStatStrip
            items={[
              { label: 'Fences', value: mission.geofences.length, color: MUTED, icon: <CropFreeIcon /> },
              { label: 'Inside', value: feed.inside, color: feed.inside > 0 ? TEAL : MUTED, icon: <LoginIcon /> },
              { label: 'Visits', value: feed.visits, color: '#42a5f5' },
              { label: 'Left', value: feed.left, color: MUTED, icon: <LogoutIcon /> },
              { label: 'Watchlist', value: feed.watchlisted, color: feed.watchlisted ? RED : MUTED, icon: <VisibilityIcon /> },
              { label: 'Assigned', value: mission.assignees.length, color: TEAL },
              { label: 'Presence', value: feed.presence.length, color: '#e8ebee' },
              { label: 'Repeat', value: feed.presence.filter((row) => row.visits > 1).length, color: '#f5b942' },
            ]}
          />

          {feed.presence.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nobody in these fences in the last 24 hours.
            </Typography>
          ) : (
            feed.presence.map((row) => (
              <PresenceCard
                key={row.key}
                row={row}
                onFocus={onFocus}
              />
            ))
          )}
        </>
      )}
    </Box>
  );
};

const PresenceCard: React.FC<{
  row: AreaPresence;
  onFocus?: (longitude: number, latitude: number) => void;
}> = ({ row, onFocus }) => {
  const dwellS = Math.max(
    0,
    (new Date(row.inside ? Date.now() : (row.leftAt || row.lastAt)).getTime()
      - new Date(row.enteredAt).getTime()) / 1000,
  );
  return (
    <Box
      onClick={() => onFocus?.(row.longitude, row.latitude)}
      sx={{
        mb: 0.75,
        pl: 0,
        display: 'grid',
        gridTemplateColumns: '3px 1fr',
        gap: 1,
        borderRadius: 1,
        cursor: onFocus ? 'pointer' : 'default',
        '&:hover': onFocus ? { bgcolor: 'rgba(255,255,255,0.03)' } : undefined,
      }}
    >
      <Box
        sx={{
          borderRadius: '0 2px 2px 0',
          bgcolor: row.watchlisted ? RED : row.inside ? TEAL : 'transparent',
        }}
      />
      <Box sx={{ py: 0.45, pr: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#e8ebee' }}>
            {row.entity}
          </Typography>
          {row.watchlisted && (
            <Chip>watchlisted</Chip>
          )}
          <Chip tone={row.inside ? TEAL : MUTED}>
            {row.inside ? 'inside' : 'left'}
          </Chip>
        </Box>
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.62rem', color: MUTED }}>
          {row.geofenceName}
          {' · '}
          {row.inside ? `since ${formatWhen(row.enteredAt)}` : formatWhen(row.leftAt || row.lastAt)}
          {dwellS >= 60 ? ` · ${formatSpan(dwellS)}` : ''}
          {row.visits > 1 ? ` · ${row.visits} visits` : ''}
        </Typography>
      </Box>
    </Box>
  );
};

const Chip: React.FC<{ tone?: string; children: React.ReactNode }> = ({
  tone = MUTED,
  children,
}) => (
  <Box
    component="span"
    sx={{
      fontSize: '0.6rem',
      fontWeight: 700,
      color: tone,
      border: `1px solid ${EDGE}`,
      bgcolor: 'rgba(255,255,255,0.04)',
      px: 0.65,
      borderRadius: 1.1,
    }}
  >
    {children}
  </Box>
);

export default MissionAreasTab;
